"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Circle } from "lucide-react";

import { cn } from "@/utils/tailwind";

// Hook to check archival mode (lazy import to avoid circular deps)
let useArchivalModeHook: (() => { isArchivalMode: boolean }) | null = null;
try {
  const archivalContext = require("@/contexts/ArchivalModeContext");
  useArchivalModeHook = archivalContext.useArchivalMode;
} catch {
  // Context not available
}

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, disabled, ...props }, ref) => {
  // Check archival mode
  let isArchivalMode = false;
  try {
    if (useArchivalModeHook) {
      const archival = useArchivalModeHook();
      isArchivalMode = archival.isArchivalMode;
    }
  } catch {
    // Context not available
  }
  
  // Disable radio group in archival mode - NO EXCEPTIONS
  const effectiveDisabled = disabled || isArchivalMode;
  
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      disabled={effectiveDisabled}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, disabled, ...props }, ref) => {
  // Check archival mode
  let isArchivalMode = false;
  try {
    if (useArchivalModeHook) {
      const archival = useArchivalModeHook();
      isArchivalMode = archival.isArchivalMode;
    }
  } catch {
    // Context not available
  }
  
  // Disable radio item in archival mode - NO EXCEPTIONS
  const effectiveDisabled = disabled || isArchivalMode;
  
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      disabled={effectiveDisabled}
      className={cn(
        "border-primary text-primary ring-offset-background focus-visible:ring-ring aspect-square h-4 w-4 rounded-full border focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
